<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-sm-6">
                <h4 class="card-title">Lista de Estudos DICOM</h4>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end">
                  <button class="btn btn-success float-right" @click="loadStudies">
                    <i class="ri-refresh-line me-1"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-sm-12 col-md-6">
                <div id="studies-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Mostrar&nbsp;
                    <b-form-select v-model="perPage" size="sm" :options="pageOptions"></b-form-select>&nbsp;registros
                  </label>
                </div>
              </div>
              <!-- Search -->
              <div class="col-sm-12 col-md-6">
                <div id="studies-table_filter" class="dataTables_filter text-md-right">
                  <label class="d-inline-flex align-items-center">
                    Buscar por Study UID:
                    <b-form-input
                      v-model="studyUidSearch"
                      type="search"
                      class="form-control form-control-sm ml-2"
                      placeholder="Digite o Study UID"
                      @keyup.enter="searchByStudyUid"
                    ></b-form-input>
                    <button class="btn btn-outline-primary btn-sm ml-2" @click="searchByStudyUid">
                      <i class="ri-search-line"></i>
                    </button>
                    <button class="btn btn-outline-secondary btn-sm ml-2" @click="clearSearch">
                      <i class="ri-close-line"></i>
                    </button>
                  </label>
                </div>
              </div>
              <!-- End search -->
            </div>

            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">                
              </div>
            </div>

            <div v-else class="table-responsive">
              <b-table
                :items="studies"
                :fields="fields"
                responsive="sm"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                @filtered="onFiltered"
              >
                <template v-slot:cell(patient_name)="row">
                  {{ row.value || 'N/A' }}
                </template>

                <template v-slot:cell(study_date)="row">
                  {{ formatDate(row.value) }}
                </template>

                <template v-slot:cell(modality)="row">
                  <span class="badge bg-soft-primary text-primary">{{ row.value || 'N/A' }}</span>
                </template>

                <template v-slot:cell(study_description)="row">
                  {{ row.value || 'N/A' }}
                </template>

                <template v-slot:cell(series_count)="row">
                  <span class="badge bg-soft-info text-info">{{ row.value || 0 }} séries</span>
                </template>

                <template v-slot:cell(instance_count)="row">
                  <span class="badge bg-soft-secondary text-secondary">{{ row.value || 0 }} imagens</span>
                </template>

                <template v-slot:cell(action)="row">
                  <a
                    href="javascript:void(0);"
                    class="mr-3 text-primary"
                    v-b-tooltip.hover
                    title="Visualizar no OHIF"
                    @click="openViewer(row.item)"
                  >
                    <i class="mdi mdi-eye font-size-18"></i>
                  </a>
                  <a
                    href="javascript:void(0);"
                    class="mr-3 text-success"
                    v-b-tooltip.hover
                    title="Obter Link do Visualizador"
                    @click="getViewerLink(row.item)"
                  >
                    <i class="mdi mdi-link font-size-18"></i>
                  </a>
                  <a
                    :href="row.item.viewer_links.orthanc_study_viewer"
                    class="text-info"
                    v-b-tooltip.hover
                    title="Detalhes"
                    @click="viewDetails(row.item)"
                  >
                    <i class="mdi mdi-information font-size-18"></i>
                  </a>
                </template>
              </b-table>
            </div>

            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Link do Visualizador -->
    <b-modal v-model="showLinkModal" title="Link do Visualizador" size="lg">
      <div v-if="viewerLink">
        <p class="mb-2"><strong>Link do Visualizador:</strong></p>
        <div class="input-group mb-3">
          <input 
            type="text" 
            class="form-control" 
            :value="viewerLink" 
            readonly
            ref="linkInput"
          />
          <button class="btn btn-outline-secondary" @click="copyLink">
            <i class="ri-file-copy-line"></i> Copiar
          </button>
        </div>
        <div class="mt-3">
          <a :href="viewerLink" target="_blank" class="btn btn-primary">
            <i class="ri-external-link-line me-1"></i> Abrir Visualizador
          </a>
        </div>
      </div>
      <div v-else class="text-center py-3">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Gerando link...</span>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { dicomService } from "@/api/dicom";

export default {
  page: {
    title: "Lista de Estudos DICOM",
    meta: [{ name: "description", content: "Lista de estudos DICOM disponíveis para visualização" }],
  },
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      title: "Lista de Estudos DICOM",
      items: [
        {
          text: "DICOM",
          href: "/dicom",
        },
        {
          text: "Estudos",
          active: true,
        },
      ],
      studies: [],
      loading: false,
      studyUidSearch: "",
      isSearching: false,
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [5, 10, 25, 50],
      filter: null,
      filterOn: [],
      sortBy: "study_date",
      sortDesc: true,
      showLinkModal: false,
      viewerLink: null,
      fields: [
        { key: "patient_name", sortable: true, label: "Paciente" },
        { key: "patient_id", sortable: true, label: "CPF" },
        { key: "study_date", sortable: true, label: "Data do Estudo" },
        { key: "modality", sortable: true, label: "Modalidade" },
        { key: "study_description", sortable: true, label: "Descrição" },
        { key: "series_count", sortable: true, label: "Séries" },
        { key: "instance_count", sortable: true, label: "Imagens" },
        { key: "action", label: "Ações" }
      ]
    };
  },
  computed: {
    /**
     * Total no. of records (from API response)
     */
    rows() {
      return this.totalRows;
    }
  },
  async created() {
    await this.loadStudies();
  },
  watch: {
    // Watch for page changes
    currentPage() {
      if (!this.isSearching) {
        this.loadStudies();
      }
    },
    // Watch for perPage changes
    perPage() {
      this.currentPage = 1; // Reset to first page
      if (!this.isSearching) {
        this.loadStudies();
      }
    }
  },
  methods: {
    async loadStudies() {
      this.loading = true;
      this.isSearching = false;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.perPage,
          limit: this.perPage,
        };
        
        const response = await dicomService.getOrthancStudies(params);
        
        // A resposta da API tem a estrutura: { data: { studies: [], total: number, offset: number, limit: number } }
        if (response && response.data && response.data.studies && Array.isArray(response.data.studies)) {
          this.studies = response.data.studies;
          this.totalRows = response.data.total || 0;
          console.log("Estudos carregados:", this.studies);
          console.log("Primeiro estudo (estrutura):", this.studies[0]);
        } else if (response && Array.isArray(response)) {
          // Fallback se a resposta for diretamente um array
          this.studies = response;
          this.totalRows = response.length;
        } else {
          console.error("Resposta da API inválida:", response);
          this.studies = [];
          this.totalRows = 0;
        }
        
      } catch (error) {
        console.error("Erro ao carregar estudos:", error);
        this.$toast.error("Erro ao carregar estudos DICOM");
        this.studies = [];
        this.totalRows = 0;
      } finally {
        this.loading = false;
      }
    },

    async searchByStudyUid() {
      if (!this.studyUidSearch.trim()) {
        this.$toast.error("Digite um Study UID para buscar");
        return;
      }

      this.loading = true;
      this.isSearching = true;
      try {
        // Buscar por study_uid específico
        const response = await dicomService.getViewerLink(this.studyUidSearch.trim());
        
        // Se encontrou, simular um resultado de busca
        if (response && response.data) {
          // Criar um objeto de estudo baseado na resposta
          const study = {
            study_uid: this.studyUidSearch.trim(),
            patient_name: response.data.patient_name || 'N/A',
            study_date: response.data.study_date || null,
            modality: response.data.modality || 'N/A',
            study_description: response.data.study_description || 'N/A',
            series_count: response.data.series_count || 0,
            instance_count: response.data.instance_count || 0,
          };
          
          this.studies = [study];
          this.totalRows = 1;
          this.currentPage = 1;
        } else {
          this.studies = [];
          this.totalRows = 0;
          this.$toast.info("Nenhum estudo encontrado com este Study UID");
        }
        
      } catch (error) {
        console.error("Erro ao buscar estudo por UID:", error);
        if (error.response?.status === 404) {
          this.studies = [];
          this.totalRows = 0;
          this.$toast.info("Nenhum estudo encontrado com este Study UID");
        } else {
          this.$toast.error("Erro ao buscar estudo");
        }
      } finally {
        this.loading = false;
      }
    },

    clearSearch() {
      this.studyUidSearch = "";
      this.isSearching = false;
      this.currentPage = 1;
      this.loadStudies();
    },

    /**
     * Search the table data with search input
     */
    onFiltered(filteredItems) {
      // Para busca local, ajustar o total de linhas
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    async openViewer(study) {
      try {
        console.log("Abrindo visualizador para estudo:", study);
        
        // Verificar se o estudo já tem a URL do visualizador
        let ohifUrl = null;
        
        if (study.viewer_links && study.viewer_links.ohif_viewer) {
          ohifUrl = study.viewer_links.ohif_viewer;
          console.log("URL do OHIF encontrada diretamente no estudo:", ohifUrl);
        } else if (study.ohif_viewer_url) {
          ohifUrl = study.ohif_viewer_url;
          console.log("URL do OHIF encontrada em ohif_viewer_url:", ohifUrl);
        } else {
          console.log("URL não encontrada no estudo, fazendo chamada para API...");
          // Fallback: buscar via API se não tiver a URL
          const response = await dicomService.getViewerLink(study.study_uid);
          console.log("Resposta da API para viewer:", response);
          
          if (response && response.data && response.data.viewer_links && response.data.viewer_links.ohif_viewer) {
            ohifUrl = response.data.viewer_links.ohif_viewer;
            console.log("URL do OHIF obtida via API:", ohifUrl);
          }
        }
        
        if (ohifUrl) {
          const viewerUrl = this.addTokenToUrl(ohifUrl);
          console.log("URL final para abrir:", viewerUrl);
          window.open(viewerUrl, '_blank');
        } else {
          console.error("Não foi possível obter URL do visualizador");
          this.$toast.error("Não foi possível obter o link do visualizador");
        }
      } catch (error) {
        console.error("Erro ao abrir visualizador:", error);
        this.$toast.error("Erro ao abrir visualizador");
      }
    },

    async getViewerLink(study) {
      this.showLinkModal = true;
      this.viewerLink = null;
      
      try {
        console.log("Obtendo link do visualizador para estudo:", study);
        
        // Verificar se o estudo já tem a URL do visualizador
        let ohifUrl = null;
        
        if (study.viewer_links && study.viewer_links.ohif_viewer) {
          ohifUrl = study.viewer_links.ohif_viewer;
          console.log("URL do OHIF encontrada diretamente no estudo:", ohifUrl);
        } else if (study.ohif_viewer_url) {
          ohifUrl = study.ohif_viewer_url;
          console.log("URL do OHIF encontrada em ohif_viewer_url:", ohifUrl);
        } else {
          console.log("URL não encontrada no estudo, fazendo chamada para API...");
          // Fallback: buscar via API se não tiver a URL
          const response = await dicomService.getViewerLink(study.study_uid);
          console.log("Resposta da API para link:", response);
          
          if (response && response.data && response.data.viewer_links && response.data.viewer_links.ohif_viewer) {
            ohifUrl = response.data.viewer_links.ohif_viewer;
            console.log("URL do OHIF obtida via API:", ohifUrl);
          }
        }
        
        if (ohifUrl) {
          this.viewerLink = this.addTokenToUrl(ohifUrl);
          console.log("URL final do modal:", this.viewerLink);
        } else {
          console.error("Não foi possível obter URL do visualizador");
          this.$toast.error("Não foi possível obter o link do visualizador");
          this.showLinkModal = false;
        }
      } catch (error) {
        console.error("Erro ao obter link do visualizador:", error);
        this.$toast.error("Erro ao obter link do visualizador");
        this.showLinkModal = false;
      }
    },

    addTokenToUrl(url) {
      // Verificar se a URL é válida
      if (!url || typeof url !== 'string') {
        console.error("URL inválida recebida:", url);
        return url || '';
      }

      try {
        console.log("=== addTokenToUrl ===");
        console.log("URL recebida:", url);
        
        // Tentar obter o token de várias fontes possíveis
        let accessToken = null;
        
        // Tentar do Vuex store - local correto: auth.currentUser.access_token
        if (this.$store && this.$store.state && this.$store.state.auth && this.$store.state.auth.currentUser) {
          accessToken = this.$store.state.auth.currentUser.access_token;
          console.log("Token encontrado no store auth.currentUser.access_token:", !!accessToken);
          if (accessToken) {
            console.log("Token valor (primeiros 20 chars):", accessToken.substring(0, 20) + "...");
          }
        }
        
        // Fallback: outros locais no store
        if (!accessToken && this.$store && this.$store.state) {
          accessToken = this.$store.state.auth?.accessToken || 
                       this.$store.state.auth?.token ||
                       this.$store.state.token ||
                       this.$store.getters?.accessToken;
          console.log("Token encontrado em fallback do store:", !!accessToken);
        }
        
        // Tentar do localStorage se não encontrou no store
        if (!accessToken) {
          accessToken = localStorage.getItem('token') || 
                       localStorage.getItem('access_token') ||
                       localStorage.getItem('authToken') ||
                       localStorage.getItem('jwt_token');
          console.log("Token encontrado no localStorage:", !!accessToken);
        }
        
        // Tentar do sessionStorage
        if (!accessToken) {
          accessToken = sessionStorage.getItem('token') || 
                       sessionStorage.getItem('access_token');
          console.log("Token encontrado no sessionStorage:", !!accessToken);
        }
        
        console.log("Token final encontrado:", accessToken ? "Sim" : "Não");
        
        if (!accessToken) {
          console.warn("Access token não encontrado em nenhuma fonte, retornando URL original");
          console.log("Store state completo:", this.$store?.state);
          return url;
        }

        // Verificar se a URL já tem parâmetros
        const separator = url.includes('?') ? '&' : '?';
        const finalUrl = `${url}${separator}token=${accessToken}`;
        console.log("URL final construída:", finalUrl);
        console.log("=== Fim addTokenToUrl ===");
        return finalUrl;
      } catch (error) {
        console.error("Erro ao adicionar token à URL:", error);
        return url || '';
      }
    },

    copyLink() {
      if (this.viewerLink && this.$refs.linkInput) {
        this.$refs.linkInput.select();
        document.execCommand('copy');
        this.$toast.success("Link copiado para a área de transferência!");
      }
    },

    viewDetails(study) {
      // Navegar para página de detalhes do estudo
      this.$router.push(`/dicom/study/${study.study_uid}`);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('pt-BR');
    },
  },
};
</script>

<style scoped>
.table th {
  border-top: none;
}

.badge {
  font-size: 0.75rem;
}

.btn-sm {
  font-size: 0.875rem;
}
</style>
